import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import type { ProcessedBranding } from '@/types/tenant';
import RecuperarSenhaForm from './RecuperarSenhaForm';

interface LoginFormProps {
  branding: ProcessedBranding;
}

export default function LoginForm({ branding }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [recoveryOpen, setRecoveryOpen] = useState(false);

  const { signIn, recoveryPending, recoveryNotice } = useAuth();

  // A verified code or an abandoned recovery keeps the card on recovery, even
  // after the form consumes the notice.
  useEffect(() => {
    if (recoveryPending || recoveryNotice) setRecoveryOpen(true);
  }, [recoveryPending, recoveryNotice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !senha) {
      setError('Por favor, preencha todos os campos');
      setLoading(false);
      return;
    }

    try {
      sessionStorage.setItem('joshua_fresh_login', 'true');
      await signIn(email, senha);
    } catch {
      sessionStorage.removeItem('joshua_fresh_login');
      // SECURITY: generic error message prevents user enumeration.
      setError('Credenciais inválidas. Verifique seu email e senha.');
    } finally {
      setLoading(false);
    }
  };

  if (recoveryOpen || recoveryPending || recoveryNotice) {
    return (
      <RecuperarSenhaForm
        branding={branding}
        initialEmail={email}
        onClose={() => setRecoveryOpen(false)}
      />
    );
  }

  return (
    <>
      {branding.logoUrl && (
        <div className="auth-card-logo-wrapper">
          <img
            src={branding.logoUrl}
            alt={`${branding.tenantName} Logo`}
            className="auth-card-logo"
          />
        </div>
      )}

      <p className="auth-greeting">Bem-vindo de volta.</p>

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label className="auth-field">
          <span className="auth-field-label">E-mail</span>
          <div className="auth-field-control">
            <span className="auth-field-icon" aria-hidden="true">
              <Mail size={16} strokeWidth={1.75} />
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@empresa.com.br"
              aria-label="E-mail"
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              autoComplete="email"
              className="auth-field-input"
              disabled={loading}
            />
          </div>
        </label>

        <label className="auth-field">
          <span className="auth-field-label">Senha</span>
          <div className="auth-field-control">
            <span className="auth-field-icon" aria-hidden="true">
              <Lock size={16} strokeWidth={1.75} />
            </span>
            <input
              type={showPassword ? 'text' : 'password'}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="••••••••"
              aria-label="Senha"
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              autoComplete="current-password"
              className="auth-field-input"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              className="auth-password-toggle"
              disabled={loading}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        {error && (
          <div className="auth-error" role="alert">
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !email || !senha}
          className="auth-button"
          style={{
            background: branding.primaryColor,
            borderColor: branding.primaryColor,
          }}
        >
          {loading ? (
            <>
              <span className="auth-button-spinner" aria-hidden="true" />
              <span>Entrando...</span>
            </>
          ) : (
            <>
              <span>Entrar</span>
              <ArrowRight size={16} strokeWidth={2} />
            </>
          )}
        </button>

        <button
          type="button"
          className="auth-link"
          onClick={() => {
            setError('');
            setRecoveryOpen(true);
          }}
          disabled={loading}
        >
          Esqueceu a senha?
        </button>
      </form>
    </>
  );
}
